import { useState } from 'react';
import { MobileSelect } from '@/components/ui';
import { useSupplierOptions } from '@/hooks';

interface CreateSupplierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (supplierId: string) => void;
  createSupplier: (name: string) => Promise<string>;
}

function CreateSupplierModal({ 
  isOpen, 
  onClose, 
  onCreated,
  createSupplier,
}: CreateSupplierModalProps) {
  const [name, setName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsCreating(true);
    setError(null);

    try {
      const newId = await createSupplier(name.trim());
      onCreated(newId);
      setName('');
      onClose();
    } catch (err) {
      console.error('Error creating supplier:', err);
      setError(err instanceof Error ? err.message : 'Failed to create supplier');
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setName('');
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-secondary-200">
          <h3 className="text-lg font-semibold text-secondary-900">
            Create New Supplier
          </h3>
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-secondary-100 transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5 text-secondary-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label
              htmlFor="supplier-name"
              className="block text-sm font-medium text-secondary-700 mb-1"
            >
              Supplier Name
            </label>
            <input
              id="supplier-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Home Depot, Lowe's"
              className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={isCreating}
              autoFocus
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isCreating}
              className="flex-1 px-4 py-2 border border-secondary-300 text-secondary-700 font-medium rounded-lg hover:bg-secondary-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating || !name.trim()}
              className="flex-1 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isCreating ? 'Creating...' : 'Create Supplier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface SupplierSelectProps {
  value: string | null;
  onChange: (supplierId: string | null) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
}

export function SupplierSelect({
  value,
  onChange,
  disabled = false,
  label = 'Supplier',
  placeholder = 'Select supplier (optional)',
}: SupplierSelectProps) {
  const { suppliers, isLoading, createNewSupplier } = useSupplierOptions();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSupplierCreated = (newId: string) => {
    // Set the newly created supplier as selected
    onChange(newId);
  };

  return (
    <>
      <div className="space-y-2">
        <MobileSelect
          label={label}
          value={value || ''}
          onChange={(val) => onChange(val || null)}
          disabled={disabled || isLoading}
          placeholder={isLoading ? 'Loading suppliers...' : placeholder}
          options={[
            { value: '', label: 'No supplier' },
            ...suppliers.map((s) => ({ value: s.id, label: s.name })),
          ]}
        />
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          disabled={disabled}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
        >
          + Create new supplier
        </button>
      </div>

      <CreateSupplierModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreated={handleSupplierCreated}
        createSupplier={createNewSupplier}
      />
    </>
  );
}